<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeletelRequiredAttributesAPI from 'dashboard/api/teletel/conversationRequiredAttributes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// The backend for this has existed since the flows batch - controller, service
// and API client all shipped. What was missing was any way for an administrator
// to reach it, so the setting could only be changed from a console.
const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const available = ref([]);
const selected = ref([]);

const isSelected = key => selected.value.includes(key);

const toggle = key => {
  selected.value = isSelected(key)
    ? selected.value.filter(k => k !== key)
    : [...selected.value, key];
};

const hasAttributes = computed(() => available.value.length > 0);
const selectedCount = computed(() => selected.value.length);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TeletelRequiredAttributesAPI.show();
    available.value = data.available_attributes || [];
    selected.value = [...(data.conversation_required_attributes || [])];
  } catch (error) {
    useAlert(t('TELETEL_REQUIRED_ATTRIBUTES.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await TeletelRequiredAttributesAPI.update(selected.value);
    // Trust the server's answer over local state: it validates the keys and
    // may return fewer than were sent.
    selected.value = [...(data.conversation_required_attributes || [])];
    useAlert(t('TELETEL_REQUIRED_ATTRIBUTES.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('TELETEL_REQUIRED_ATTRIBUTES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('TELETEL_REQUIRED_ATTRIBUTES.TITLE') }}
        </h1>
        <p class="mt-1 mb-0 text-sm text-n-slate-11 max-w-prose">
          {{ t('TELETEL_REQUIRED_ATTRIBUTES.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        v-if="hasAttributes"
        blue
        sm
        :is-loading="saving"
        :disabled="loading || saving"
        @click="save"
      >
        {{ t('TELETEL_REQUIRED_ATTRIBUTES.SAVE') }}
      </NextButton>
    </div>

    <div v-if="loading" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('TELETEL_REQUIRED_ATTRIBUTES.LOADING') }}
    </div>

    <!--
      No custom attributes means there is nothing this page can require, so say
      that and point at where they are created rather than showing an empty box.
    -->
    <div
      v-else-if="!hasAttributes"
      class="flex flex-col items-center gap-2 px-6 py-10 text-center border rounded-xl border-n-weak"
    >
      <Icon icon="i-lucide-list-checks" class="size-6 text-n-slate-10" />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('TELETEL_REQUIRED_ATTRIBUTES.EMPTY') }}
      </p>
    </div>

    <template v-else>
      <p class="mb-0 text-xs text-n-slate-10">
        {{
          t('TELETEL_REQUIRED_ATTRIBUTES.SELECTED_COUNT', {
            count: selectedCount,
          })
        }}
      </p>

      <ul class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="attribute in available"
          :key="attribute.attribute_key"
          class="flex items-center gap-3 px-3 py-2.5 border rounded-lg cursor-pointer border-n-weak hover:border-n-slate-7"
          :class="
            isSelected(attribute.attribute_key)
              ? 'bg-n-brand/5 border-n-brand'
              : ''
          "
          @click="toggle(attribute.attribute_key)"
        >
          <input
            type="checkbox"
            class="m-0 shrink-0"
            :checked="isSelected(attribute.attribute_key)"
            :aria-label="attribute.attribute_display_name"
            @click.stop="toggle(attribute.attribute_key)"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-sm truncate text-n-slate-12">
              {{ attribute.attribute_display_name }}
            </span>
            <span class="text-xs truncate text-n-slate-10">
              {{ attribute.attribute_key }}
            </span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>
